import React from "react";

import { type baseTheme } from "../../theme/theme";

import styles from "./Typography.module.css";

type ColorScale = keyof typeof baseTheme.color.stone;
type Color = "white" | "muted" | "card" | `stone-${ColorScale}`;

export interface TypographyProps {
  /**
   * Text content
   */
  children: React.ReactNode;
  /**
   * Typography variant
   */
  variant?: "h1" | "h2" | "body" | "small" | "caption";
  /**
   * Font weight override
   */
  weight?: "regular" | "medium";
  /**
   * Text color from theme
   */
  color?: Color;
  className?: string;
}

export const Typography = ({
  children,
  variant = "body",
  weight,
  color,
  className,
  style,
}: TypographyProps & { style?: React.CSSProperties }): JSX.Element => {
  const variantClass = [
    styles[variant],
    weight === "medium" ? styles.weightMedium : undefined,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // inline style for color — semantic tokens override stone-* palette
  const resolveColor = (c?: Color): React.CSSProperties => {
    if (!c) return {}; // inherit from CSS class (--color-text)
    switch (c) {
      case "white":
        return { color: "white" };
      case "muted":
        return { color: "var(--color-text-muted)" };
      case "card":
        return { color: "var(--color-card-text)" };
      default:
        return { color: `var(--color-${c})` };
    }
  };
  const colorStyle = resolveColor(color);

  const mergedStyle = { ...colorStyle, ...style };

  if (variant === "h1" || variant === "h2") {
    return React.createElement(
      variant,
      {
        className: variantClass,
        style: mergedStyle,
      },
      children
    );
  }

  return (
    <p className={variantClass} style={mergedStyle}>
      {children}
    </p>
  );
};
